/* eslint no-shadow: ["error", { "allow": ["state", "getters"] }] */
/* eslint object-curly-newline: ["error", { "multiline": true }] */
/* eslint no-param-reassign: ["error", { "props": false }] */
import moment from 'moment';
import lodash from 'lodash';
import axios from 'axios';
import qs from 'qs';

// initial state
const state = {
  search: null,
  limit: 9,
  includedStatuses: ['Member Review', 'In Progress', 'Complete', 'Killed'],
  filters: {},
  dateTo: moment().format('YYYY-MM-DD'),
  dateFrom: moment().add(1, 'months').format('YYYY-MM-DD'),
  dateList: [moment().format('YYYY-MM-DD'), '2014-01-01'],
  allWorkOrders: [],
  filtersShown: [],
  filtersChanged: [],
  displayStatusList: [],
  departments: [],
  departmentsList: [
    'Marketing',
    'Digital',
    'Professional Development',
    'Human Resources',
    'Finance and Operations',
  ],
  objectives: [],
  objectivesList: [
    'New Patient Acquisition',
    'Existing Patient Engagement',
    'Practice Branding',
    'Practice Management',
  ],
  specialties: [],
  specialtiesList: [
    'Audiology',
    'Tinnitus',
    'Cochlear',
    'Allergy',
    'Sinus',
    'Sleep',
    'Vision',
    'Cosmetics',
    'General ENT',
  ],
  dashboardData: {
    hoursWorked: -1,
    activeWOs: 0,
    completedWOs: 0,
    totalWorkOrders: 0,
    countByType: {},
    workOrders: {},
  },
  loaders: {
    dateList: false,
    workOrders: false,
    filters: false,
    dashboardData: true,
  },
};

// getters
const getters = {
  search: (state) => state.search,
  limit: (state) => state.limit,
  includedStatuses: (state) => state.includedStatuses,
  dateFrom: (state) => moment(state.dateFrom).toDate(),
  dateTo: (state) => moment(state.dateTo).toDate(),
  dateList: (state) => state.dateList,
  disabledRangeFrom: (state) => ({
    to: moment(state.dateList[state.dateList.length - 1]).toDate(),
    from: state.dateTo ? moment(state.dateTo).toDate() : moment(state.dateList[0]).endOf('month').toDate(),
  }),
  disabledRangeTo: (state) => ({
    to: state.dateFrom ? moment(state.dateFrom).toDate() : moment(state.dateList[state.dateList.length - 1]).toDate(),
    from: state.dateList.length > 0 ? moment(state.dateList[0]).endOf('month').toDate() : moment().endOf('month').toDate(),
  }),
  filters: (state) => ({
    dateFrom: moment(state.dateFrom).format('YYYY-MM-01'),
    dateTo: moment(state.dateTo).endOf('month').format('YYYY-MM-DD'),
    departments: state.departments,
    objectives: state.objectives,
    specialties: state.specialties,
  }),
  dashboardData: (state) => state.dashboardData,
  displayStatusList: (state) => state.displayStatusList,
  filtersShown: (state) => state.filtersShown,
  filtersChanged: (state) => state.filtersChanged,
  departments: (state) => state.departments,
  departmentsList: (state) => lodash.map(state.departmentsList, (department) => ({ label: department, value: department })),
  objectives: (state) => state.objectives,
  objectivesList: (state) => lodash.map(state.objectivesList, (objective) => ({ label: objective, value: objective })),
  specialties: (state) => state.specialties,
  specialtiesList: (state) => lodash.map(state.specialtiesList, (specialty) => ({ label: specialty, value: specialty })),
  loaders: (state) => state.loaders,
  allWorkOrders: (state) => state.allWorkOrders,
  sortedWorkOrders: (state, getters) => lodash.chain(getters.allWorkOrders)
    // we only want to include certain statuses so we remove all other work orders
    .filter((workOrder) => (state.includedStatuses).includes(workOrder.portalStatus))
    .filter((workOrder) => (!state.search || (state.search && workOrder.title.includes(state.search))))
    // sort by last_action property (will be asc)
    .sortBy('last_action')
    // reverse this because we want most recent
    .reverse()
    // take the number of most recent work orders defined by limit
    .take(state.limit)
    // get the value from the chain
    .value(),
  workOrderObject: (state, getters) => {
    const workOrderObject = {};
    lodash.map(state.includedStatuses, (status) => {
      workOrderObject[status] = lodash.filter(
        getters.sortedWorkOrders,
        (workOrder) => workOrder.portalStatus === status,
      );
    });
    return workOrderObject;
  },
  workOrders: (state, getters) => {
    return lodash.chain(getters.workOrderObject)
      // convert object to an array of arrays of work orders
      .values()
      // flatten to one array
      .flatten()
      // get the value from the chain
      .value();
  },
};

// actions
const actions = {
  setProperty({ commit }, payload) {
    commit('SET_PROPERTY', payload);
  },

  getDateList({ commit, dispatch, state }) {
    commit('TOGGLE_LOADING', { key: 'dateList', value: true });
    return axios.get('/api/fuel-utilization/filter/workOrderMonths/', { params: state.filters })
      .then((response) => {
        commit('SET_PROPERTY', { key: 'dateList', value: response.data });
        commit('TOGGLE_LOADING', { key: 'dateList', value: false });
        dispatch('main/setProperty', { key: 'globalLoading', value: false }, { root: true });
      });
  },

  /**
   * Loads list of work orders
   */
  getWorkOrders({ commit, dispatch, getters }) {
    commit('TOGGLE_LOADING', { key: 'workOrders', value: true });
    axios.get(`/api/fuel-utilization/workOrders/?${qs.stringify(getters.filters, { arrayFormat: 'brackets' })}`)
      .then((response) => {
        commit('SET_PROPERTY', { key: 'allWorkOrders', value: response.data });
        commit('TOGGLE_LOADING', { key: 'workOrders', value: false });
        dispatch('main/setProperty', { key: 'globalLoading', value: false }, { root: true });
      });
  },

  /**
   * Loads list of work orders
   */
  getDashboardWorkOrders({ commit }, payload) {
    commit('TOGGLE_LOADING', { key: 'dashboardData', value: true });
    axios.get(`/api/fuel-utilization/workOrdersOverview/?${qs.stringify(payload)}`)
      .then((response) => {
        commit('TOGGLE_LOADING', { key: 'dashboardData', value: false });
        commit('SET_PROPERTY', { key: 'dashboardData', value: response.data });
      });
  },
};

// mutations
const mutations = {
  SET_PROPERTY(state, payload) {
    state[payload.key] = payload.value;
  },
  TOGGLE_LOADING(state, payload) {
    state.loaders[payload.key] = payload.value;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
